<script setup>
// Astra DS · HeroAgenda — la mitad derecha del hero de la portada.
//
// Figma: `Template/Home-Reset 659:2862` → `hero 1380:181` → `agenda 1380:187`.
// Datos: `CONCIERTOS_WEB_INSTRUCCIONES.md` (01.09.26).
//
// ── Por qué el cartel no lleva texto encima ──
//
// 🔴 Es lo que distingue esta opción de las otras dos que se dibujaron, y está
// escrito en `DESIGN.md` § **El velo es de foto, la franja es de dato**: si la
// imagen la controlamos, velo; si viene de fuera, franja. **El cartel lo entrega
// quien promociona el concierto**, así que apoyar una frase encima es apostar a
// que tenga una esquina oscura, y esa apuesta se pierde el día que no la tiene.
//
// Los datos bajan a una fila de celdas con filete, que es como esta casa escribe
// cuándo y dónde.
//
// ── La regla de 2 px ──
//
// El carrusel gira solo y la regla dice cuánto queda del paso. Es la gramática
// de `Transport` —pista en `border/strong`, relleno en `bg/accent`, 2 px, arriba
// del todo— y aquí el rojo **sí** es acción: se mueve mientras pasa algo.
//
// Con un solo concierto no hay paso que contar: ni regla, ni flechas, ni «01 /
// 05». No se gira sobre uno mismo.
//
// ⚠️ Este componente tiene **un solo consumidor**, la portada. Vive en `ds/` y no
// dentro de `HomeView` porque trae temporizador, teclado y estado propios, no
// porque sea del sistema: en Figma la agenda está dibujada en la plantilla y no
// es un `Pattern`.
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from "vue";
import DuotoneArt from "@/components/ds/DuotoneArt.vue";
import CornerTag from "@/components/ds/CornerTag.vue";
import ButtonDs from "@/components/ds/ButtonDs.vue";
import CtaButton from "@/components/ds/CtaButton.vue";
import BrandTicketmaster from "@/components/ds/BrandTicketmaster.vue";
import { MESES } from "@/services/conciertos";
import { medir } from "@/services/medicion";

const props = defineProps({
  // Lo que devuelve `getAgenda()`: un paso por artista, ya ordenado.
  conciertos: { type: Array, default: () => [] },

  // Lo que dura cada paso. Ocho segundos es lo que tarda en leerse una franja
  // —dos datos y una acción— sin que quien mira sienta que le corren la página.
  intervalo: { type: Number, default: 8000 },
});

const paso = ref(0);
// De 0 a 1. Se pinta como ancho de la regla, y se reinicia en cada salto.
const avance = ref(0);

let reloj = null;
let desde = 0;

const total = computed(() => props.conciertos.length);
const gira = computed(() => total.value > 1);
const actual = computed(() => props.conciertos[paso.value] || null);

/**
 * Qué imagen pinta la agenda: **el cartel de esa fecha, y nada más**.
 *
 * 🔴 El módulo anuncia un concierto, y lo que anuncia un concierto es su cartel:
 * lo sube Pablo desde la sección de conciertos de la administración justamente
 * para esto. **El retrato del artista no tiene nada que ver con el concierto** y
 * aquí no entra; una fecha sin cartel no llega, la descarta `getAgenda()`.
 *
 * ⚠️ Aquí hubo un respaldo al retrato «mejor que un hueco gris», y era
 * exactamente lo que hacía que el carrusel enseñara artistas en vez de
 * conciertos. Retirado el 21.09.26.
 */
const arte = computed(() => actual.value?.cartel || "");

/**
 * ── Cómo se releva un cartel por el siguiente ──
 *
 * 🔴 **Dos capas, no una.** Encargo de Pablo (22.09.26): «¿aquí es necesario
 * mostrar la carga del spinner? Si no es así, aplica una transición suave entre
 * imágenes». Tenía razón: el indicador se justifica cuando **no hay nada que
 * enseñar**, y en un carrusel que gira solo cada ocho segundos salía en cada
 * vuelta, parpadeando sobre un cartel que ya habíamos visto.
 *
 * Así que el cartel que se ve **no se va hasta que el siguiente está**. La capa
 * de abajo guarda lo que ya está pintado; la de arriba trae lo que entra, y solo
 * aparece cuando ha terminado de cargar. Al aparecer, releva a la de abajo.
 *
 * El indicador queda para lo único que no tiene relevo: **la primera vez**,
 * cuando abajo no hay nada.
 */
const mostrado = ref("");
const entrante = ref("");

/** Si la capa que está entrando ya terminó de cargar. */
const cargada = ref(false);

/** El indicador solo cuando no hay ningún cartel debajo al que volver. */
const primeraVez = computed(() => !!arte.value && !mostrado.value);

/**
 * El cartel del paso siguiente, pedido por detrás.
 *
 * 🔴 Encargo de Pablo (22.09.26): «al ser las imágenes del carrusel más pesadas
 * necesitan una precarga». Sin esto, cada salto de paso empezaba de cero y el
 * indicador de carga salía **en cada vuelta**, que es peor que el gris de
 * antes: lo que se mueve llama más que lo que está quieto.
 *
 * ⚠️ **Solo el siguiente, y solo cuando el actual ya está.** Precargarlos todos
 * son 12 MB de golpe en una portada, y a quien entra con datos le da igual el
 * tercer concierto si aún no ha visto el primero.
 *
 * ⚠️ **Y no con la conexión pidiendo ahorro.** Si el navegador dice `saveData`,
 * no se adelanta nada: cada byte que pedimos de más lo paga quien menos puede.
 */
// ⚠️ **Estuvo apagada del 22 al 23.09.26, y por un buen motivo.**
// `/conciertos/images/` respondía con **dos cabeceras `Cache-Control`
// contradictorias** —`no-store…` y `public, max-age=86400`—; ganaba `no-store` y
// el navegador no guardaba nada, así que adelantar el cartel siguiente no
// adelantaba nada y **duplicaba la descarga**. El servidor dejó una sola el
// 23.09.26, comprobado, y con eso esto pasa a valer la pena.
function precargaElSiguiente() {
  if (typeof window === "undefined" || total.value < 2) return;
  if (navigator.connection?.saveData) return;

  const siguiente = props.conciertos[(paso.value + 1) % total.value]?.cartel;
  if (!siguiente) return;

  const img = new Image();
  img.decoding = "async";
  img.src = siguiente;
}

let relevo = null;

function alCargarElCartel() {
  cargada.value = true;
  precargaElSiguiente();

  // El fundido dura 320 ms: pasado ese tiempo la capa de arriba ya está opaca y
  // la de abajo no se ve, así que se baja sin que se note.
  clearTimeout(relevo);
  relevo = setTimeout(() => {
    mostrado.value = entrante.value;
    entrante.value = "";
  }, 320);
}

// Cada cambio de cartel empieza un relevo. Se compara la URL y no el paso: si
// dos fechas comparten cartel no hay nada que relevar.
watch(
  arte,
  (nuevo) => {
    if (nuevo === mostrado.value) {
      entrante.value = "";
      return;
    }
    clearTimeout(relevo);
    cargada.value = false;
    entrante.value = nuevo;
  },
  { immediate: true },
);

onUnmounted(() => clearTimeout(relevo));

// ── La medida del cartel, y por qué se recorta lo que se recorta ──
//
// 🔴 **La caja es 2,09 y no se mueve.** Medido en producción el 21.09.26 de
// 1280 a 3440 de ventana: entre 2,0891 y 2,0926. Su alto no lo pone esto, lo
// pone el cuadrado de emisión de al lado.
//
// 🔴 **Por eso el cartel se crea a 2,09: `2508 × 1200 px`, tope 700 KB.**
// Elegido por Pablo el 21.09.26, que es quien los sube por la administración;
// el número vive aquí para quien vuelva a mirar esta caja. **Lo que no puede
// cambiar es la proporción**: el ancho puede subir, 2,09 no.
//
// Ese ancho cubre una pantalla Retina hasta 1920 de ventana —la caja mide 1235
// ahí, que son 2470 puntos reales— y de sobra el portátil de 1440, donde son
// 1820. Un cartel de 800 se estiraba 2,3 veces, y en un cartel **el texto
// delata el estirado mucho antes que una foto**.
//
// ⚠️ **Aquí hubo escrito que «ningún cartel tiene su nombre pegado al borde»,
// y era falso.** Con carteles de 800×355 —2,254 en una caja de 2,09— se come el
// 7,2 % del ancho, 3,6 por lado. En dos de los tres de septiembre no se notaba,
// porque el nombre iba centrado; en el de Catupecu Machu empieza a un 2,5 % del
// canto y **se cortaba la «C»**. Lo vio Pablo el 21.09.26 y lo reproduje en
// producción. Esa frase, escrita mirando solo los carteles que había entonces,
// es la que me hizo dar por buena la medida que pedí.
//
// Y el recorte no se puede evitar desde aquí: el navegador solo sabe recortar o
// dejar franjas, y las franjas —letterbox gris arriba y abajo— ya las descartó
// Pablo. La proporción se arregla en el cartel, no en la caja.
//
// ⚠️ **Divergencia declarada con el contrato** (`CONCIERTOS_WEB_INSTRUCCIONES.md`,
// 22.09.26): el servidor dice que el cartel «no se recorta: proporción
// original». Aquí se rellena la caja, así que un cartel que NO venga a 2,09 sí
// pierde algo. Es a propósito y lo eligió Pablo: entre recortar un poco y dejar
// franjas, franjas no. Mientras el cartel se cree a 2508 × 1200 las dos cosas
// coinciden y no se recorta nada —medido el 22.09.26: 0,09 % del alto—. Si
// algún día llega uno con otra proporción, el fallo está en el cartel.
//
// ⚠️ Y aquí **todo lo que entra es un cartel**: no hay mezcla de proporciones
// que atender, porque el retrato cuadrado del artista ya no llega a este módulo.


const dosDigitos = (n) => String(n).padStart(2, "0");

/**
 * La línea de datos, bajo el nombre: «12 SEP 2026 · KAFE ANTZOKIA · BILBAO».
 *
 * El nodo la escribe **en una sola línea con puntos medios**, no en celdas
 * separadas con su rótulo: quién toca es el titular, y cuándo y dónde son su
 * pie. Se salta lo que venga vacío —`sala` y `ciudad` pueden ser `null`— para
 * que nunca queden dos puntos seguidos.
 *
 * Si el artista tiene más fechas se dice, y **pegado a la fecha**, no al final:
 * §5 del contrato quiere «una tarjeta por artista con sus fechas dentro» y aquí
 * no cabe la lista, pero sí cabe que no desaparezcan sin dejar rastro. Al final
 * de la línea no servía —con una sala larga se cortaba antes de llegar, medido
 * con A Flock of Seagulls—, y además va donde le toca: habla de la fecha.
 *
 * ⚠️ El tope por artista **lo mueve la redacción entre 1 y 10**: no es un caso
 * raro que se pueda ignorar.
 */
const linea = computed(() => {
  const c = actual.value;
  if (!c) return "";
  const extra = c.fechas > 1 ? `+${c.fechas - 1} ${c.fechas === 2 ? "fecha" : "fechas"}` : "";
  // ⛔ **Sin año.** `DESIGN.md` § La fecha va en casilla: «día y mes bastan; el
  // año no se dibuja; la cadena de meta se queda en sala y ciudad». La primera
  // versión de esta casilla lo dejó en el pie —era lo que quedaba de la fecha al
  // sacar el día de la línea— y sobra: los conciertos de la agenda son de este
  // año o del que viene, y decirlo cuesta sitio en la línea que menos tiene.
  return [extra, c.donde].filter(Boolean).join(" · ");
});

// ── La fecha, en su propia celda ──
//
// 🔴 **El día es un dato de un vistazo, no una palabra que se lee.** La franja
// lo escribía dentro de la línea de pie —«04 SEP 2026 · RAZZMATAZZ 3 ·
// BARCELONA»—, en `mono/micro` y en gris, al mismo peso que la sala: para saber
// cuándo tocaban había que leer la línea entera. Ahora abre la franja en una
// celda propia, con el día a tamaño de titular y el mes debajo.
//
// Es la misma gramática que el resto de la casa: la celda de la izquierda es el
// dato que ordena —como la hora en la parrilla—, y lo demás cuelga de ella.
//
// ⚠️ Se derivan de `fecha`, el ISO del contrato, y no se parsea `cuando`: ese
// texto ya viene compuesto para leerse, y descomponerlo sería deshacer a mano lo
// que el servicio acaba de hacer.
const partes = computed(() => {
  const [y, m, d] = String(actual.value?.fecha || "").slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return null;
  return { dia: String(d).padStart(2, "0"), mes: MESES[m - 1] };
});

const dia = computed(() => partes.value?.dia || "");
const mes = computed(() => partes.value?.mes || "");

// ── Los tres estados de la casilla ──
//
// Especificación: `La casilla de fecha` (03.09.26), Figma `fecha 1445:146` y su
// tablero `fecha · estados 1449:2`. Reposo, mañana y hoy.
//
// 🔴 **El día se compara en `Europe/Madrid`, no con el reloj de quien mira.** Un
// concierto en Bilbao no es «mañana» porque alguien lo abra desde México. El
// truco del `sv-SE` es que ese idioma escribe la fecha en ISO, así que sale
// `2026-09-03` sin componerla a mano.
//
// ⚠️ **Se compara por día, no por hora.** Un concierto que empieza a las 21:00
// sigue siendo «hoy» a las 23:30, hasta las doce. Apagarlo al empezar exigiría
// saber la hora, y el contrato solo trae la fecha.
function diaEnMadrid() {
  return new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Madrid" });
}

const hoy = ref(diaEnMadrid());

const estadoFecha = computed(() => {
  const f = String(actual.value?.fecha || "").slice(0, 10);
  if (!f) return "";
  const dias = (new Date(f) - new Date(hoy.value)) / 86400000;
  if (dias === 0) return "hoy";
  if (dias === 1) return "manana";
  // ⚠️ La escala se para en mañana: ni «en 2 días», ni cuenta atrás. Y un
  // concierto pasado no tiene estado — o sale de la agenda o no sale.
  return "";
});

const avisoFecha = computed(() =>
  estadoFecha.value === "hoy" ? "Hoy" : estadoFecha.value === "manana" ? "Mañana" : "",
);

/**
 * Lo que falta para la próxima medianoche **de Madrid**, en milisegundos.
 *
 * Sin esto, una pestaña abierta desde ayer se queda con «mañana» en la casilla
 * de un concierto que ya es hoy. Se recalcula al dar la vuelta el día, no en
 * cada render.
 */
function hastaMedianoche() {
  const partes = new Intl.DateTimeFormat("es-ES", {
    timeZone: "Europe/Madrid",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(new Date());
  const dato = (tipo) => Number(partes.find((p) => p.type === tipo)?.value || 0);
  const segundos = dato("hour") * 3600 + dato("minute") * 60 + dato("second");
  // Un segundo de margen, para no despertar justo en el filo del cambio.
  return (86400 - segundos) * 1000 + 1000;
}

let relojDia = null;

function vigilarElDia() {
  relojDia = setTimeout(() => {
    hoy.value = diaEnMadrid();
    vigilarElDia();
  }, hastaMedianoche());
}
const contador = computed(() => `${dosDigitos(paso.value + 1)} / ${dosDigitos(total.value)}`);

// A dónde lleva «COMPRAR ENTRADAS»: a las entradas, y a nada más. Sin ellas la
// celda no se pinta, que es la regla de siempre aquí: una acción que no lleva a
// ningún sitio es peor que no ofrecerla.
//
// ⚠️ **Aquí había una caída a `/artist/<slug>` y se retira el 25.09.26.** Esa
// ruta murió con WordPress el 24.09.26 y hoy es un 301 a `/radar`: quien
// pulsaba buscando entradas acababa en el buzón de maquetas. Y con el botón
// diciendo «Comprar entradas», mandarle a otro sitio sería mentirle.
//
// ⚠️ **Qué enseña la celda cuando no hay entradas está sin decidir**, y diseño
// lo dice en su entrega. Hasta que lo decidan, no se pinta.
const destino = computed(() => {
  if (!actual.value?.entradas) return null;
  return { href: actual.value.entradas, vendedor: actual.value.vendedor || "" };
});

/**
 * ⚠️ **Quien pide no animar, no ve animación.** El carrusel se queda quieto en
 * el primero y las flechas siguen funcionando: la agenda entera se puede
 * recorrer a mano.
 */
function quietos() {
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}

function parar() {
  if (reloj) cancelAnimationFrame(reloj);
  reloj = null;
}

/**
 * Un solo `requestAnimationFrame` mueve la regla y salta de paso cuando llega
 * al final. Con un `setInterval` para el salto y otro para la regla, los dos se
 * separan en cuanto la pestaña se va a segundo plano y la regla se queda a
 * medias sobre un cartel que ya cambió.
 */
function arrancar() {
  parar();
  avance.value = 0;
  if (!gira.value || quietos()) return;

  desde = performance.now();

  const tic = (ahora) => {
    const parte = (ahora - desde) / props.intervalo;
    if (parte >= 1) {
      siguiente();
      return;
    }
    avance.value = parte;
    reloj = requestAnimationFrame(tic);
  };

  reloj = requestAnimationFrame(tic);
}

function siguiente() {
  if (!total.value) return;
  paso.value = (paso.value + 1) % total.value;
  arrancar();
}

function anterior() {
  if (!total.value) return;
  paso.value = (paso.value - 1 + total.value) % total.value;
  arrancar();
}

// La lista llega después que el componente —la portada la pide al montar—, así
// que el reloj no puede arrancar una sola vez: arranca cuando hay algo que
// pasar, y vuelve al principio si la agenda cambia debajo.
watch(
  () => props.conciertos,
  () => {
    paso.value = 0;
    arrancar();
  },
);

// Con la pestaña oculta no se gira: nadie lo está viendo y el reloj seguiría
// gastando fotogramas.
function alCambiarVisibilidad() {
  if (document.hidden) parar();
  else arrancar();
}

// ── La entrada de «hoy» ──
//
// 🔴 **Solo se anima «hoy»**, y una vez por turno. Mañana y reposo entran
// quietos: el movimiento es parte de la jerarquía, y si mañana también se
// barriera, el barrido pasaría a significar «pronto» en vez de «es hoy».
//
// ⚠️ Aquí no hay una diapositiva por concierto: la franja es **una sola celda
// que se rellena**. Así que «entrar» no es montar un nodo, es que cambie el paso
// del carrusel, y para que la animación vuelva a correr hay que quitar la clase,
// forzar el reflow y devolverla. Sin ese `offsetWidth` el navegador agrupa las
// dos escrituras y no vuelve a arrancar nada.
const casilla = ref(null);
const entrando = ref(false);

async function anunciarHoy() {
  entrando.value = false;
  if (estadoFecha.value !== "hoy") return;
  await nextTick();
  void casilla.value?.offsetWidth;
  entrando.value = true;
}

watch([paso, estadoFecha], anunciarHoy);

onMounted(() => {
  arrancar();
  vigilarElDia();
  anunciarHoy();
  document.addEventListener("visibilitychange", alCambiarVisibilidad);
});

onUnmounted(() => {
  parar();
  if (relojDia) clearTimeout(relojDia);
  document.removeEventListener("visibilitychange", alCambiarVisibilidad);
});
</script>

<template>
  <section v-if="actual" class="agenda" aria-label="Próximos conciertos">
    <!-- La regla de progreso, arriba del cartel y a todo su ancho. -->
    <div v-if="gira" class="agenda__progreso" aria-hidden="true">
      <i class="agenda__hecho" :style="{ width: `${avance * 100}%` }" />
    </div>

    <div class="agenda__cartel">
      <!-- 🔴 El cartel **no lleva color al pasar**: no es un enlace. La regla de
           `DuotoneArt` es que el color anuncia que algo lleva a alguna parte, y
           aquí quien lleva es la celda de la franja. -->
      <!--
        Sin imagen queda el hueco del arte de la casa, gris y liso, que es lo que
        `DuotoneArt` pinta sin `src`. El contrato admite «placeholder o tarjeta
        tipográfica» y aquí llegó a haber tarjeta —el nombre en grande sobre el
        gris—, pero desde que la franja empieza por el nombre eso lo decía dos
        veces en la misma columna. Hoy más de la mitad de la agenda llega sin
        cartel, así que no es un caso de borde.
      -->
      <!-- 🔴 **Y tampoco lleva el gris**, que es la excepción a «el color es
           información» (`DESIGN.md`). Encargo de Pablo (01.09.26): el cartel
           salía lavado, y no es efecto óptico —`grayscale(1) contrast(1.15)`
           aplasta un cartel, que ya viene con su propio contraste y su propio
           color decididos por quien lo diseñó, y con letra pequeña dentro de la
           imagen que el `contrast` recorta—. La regla del gris se escribió para
           portadas y fotos nuestras, donde el color anuncia directo; aquí no
           anuncia nada, porque este hueco no vuelve a color ni al pasar ni en
           directo: solo restaba calidad, siempre. -->
      <!-- 🔴 **Prioritaria.** Es lo más grande del primer pantallazo y desde el
           21.09.26 los carteles llegan a 2508 × 1200 en PNG, hasta 4,5 MB. Con
           la carga aplazada el navegador la pedía después de todo lo demás, y es
           justo la imagen por la que se juzga si la portada entra rápido. -->
      <!-- La capa de abajo: el cartel que ya está puesto. Se queda hasta que el
           de arriba termina de cargar, y por eso entre paso y paso no hay hueco
           ni indicador. -->
      <DuotoneArt
        v-if="mostrado"
        class="agenda__arte is-lista"
        :src="mostrado"
        alt=""
        :hover-color="false"
        :duotone="false"
        position="center"
        prioritaria
      />

      <!-- La de arriba: lo que entra. Aparece cuando ha cargado, no antes. -->
      <DuotoneArt
        v-if="entrante"
        :key="entrante"
        class="agenda__arte"
        :class="{ 'is-lista': cargada }"
        :src="entrante"
        :alt="actual ? `Cartel de ${actual.artista}` : ''"
        :hover-color="false"
        :duotone="false"
        position="center"
        prioritaria
        @cargada="alCargarElCartel"
      />

      <!-- 🔴 **Solo la primera vez.** No es decoración: con ficheros de hasta
           4,5 MB el hueco se queda varios segundos vacío, y un hueco quieto no
           se distingue de un módulo roto. Pero en los saltos del carrusel no
           sale, porque ahí sí hay algo que enseñar: el cartel anterior, que se
           queda hasta que el siguiente está (Pablo, 22.09.26).
           ⚠️ Es un cuadrado, no un círculo: en esta casa no hay radios. Gira
           sobre sí mismo con un canto en acento, que es el único gesto de
           «esto sigue vivo» que el sistema ya usa —el punto de señal—. -->
      <div v-if="primeraVez && !cargada" class="agenda__cargando" role="status">
        <i class="agenda__giro" aria-hidden="true" />
        <span class="agenda__cargando-texto">Cargando el cartel</span>
      </div>

      <CornerTag anchor="top">En directo · Agenda</CornerTag>
    </div>

    <div class="agenda__franja">
      <!-- La fecha abre la franja: el día grande y el mes debajo. Es la celda
           que ordena, como la hora en la parrilla. Si el contrato no trae una
           fecha legible no se pinta la celda, y la franja empieza por el
           nombre, que es como estaba antes. -->
      <div
        v-if="dia"
        ref="casilla"
        class="agenda__fecha"
        :class="[estadoFecha && `is-${estadoFecha}`, { 'is-entrando': entrando }]"
      >
        <span class="agenda__dia">{{ dia }}</span>

        <!-- 🔴 La cifra del día NO se sustituye por la palabra: si el «03»
             desaparece, la columna deja de ser una fecha. Lo que cambia es el
             renglón de abajo. -->
        <span v-if="!estadoFecha" class="agenda__mes">{{ mes }}</span>
        <span v-else class="agenda__aviso">
          <i aria-hidden="true"></i><span>{{ avisoFecha }}</span>
        </span>
      </div>

      <!-- El nombre manda y los datos van debajo en una línea. Es la celda que
           se estira, así que el mando de la derecha se queda pegado al borde. -->
      <div class="agenda__evento">
        <p class="agenda__artista">{{ actual.artista }}</p>
        <p class="agenda__linea">{{ linea }}</p>
      </div>

      <template v-if="gira">
        <p class="agenda__paso">{{ contador }}</p>

        <!-- Las dos flechas son el botón del sistema en su forma de icono. La
             celda de 56 y su filete izquierdo son de la franja, no del botón. -->
        <ButtonDs
          class="agenda__flecha"
          variant="ghost"
          icono="chevron-back"
          :icono-tam="20"
          etiqueta="Concierto anterior"
          :lado="56"
          @click="anterior"
        />

        <ButtonDs
          class="agenda__flecha"
          variant="ghost"
          icono="chevron-forward"
          :icono-tam="20"
          etiqueta="Concierto siguiente"
          :lado="56"
          @click="siguiente"
        />
      </template>

      <!-- La acción de la franja es comprar entradas. Entrega de diseño
           `2026-09-25-comprar-entradas.md`; Figma `comprar entradas 1380:213`
           con `cta · comprar entradas 2242:3` dentro.

           🔴 **El botón es el del sistema, no una receta de aquí.** Encargo de
           Pablo, 25.09.26: «debe seguir la línea de interacción como el
           resto». Antes esta celda tenía su propio enlace con su propio hover,
           y era el único sitio de la franja que no se comportaba como los
           demás botones de la casa.

           🔴 **Desde el 27.09.26 lleva uno de dos botones**, según quién venda
           (`2026-09-27-ticketmaster-o-contorno.md`, Figma `2270:27620`):
           `Brand/Ticketmaster 2268:202` si es Ticketmaster, el contorno si es
           cualquier otro. Lo dice `vendedor`, que manda el servidor; aquí no se
           adivina por el enlace. La celda abraza lo que lleva: 176 con uno,
           232 con el otro. -->
      <div
        v-if="destino?.href"
        class="agenda__entradas"
        :class="{ 'agenda__entradas--tm': destino.vendedor === 'ticketmaster' }"
      >
        <BrandTicketmaster
          v-if="destino.vendedor === 'ticketmaster'"
          :href="destino.href"
          @click="medir('concierto_entradas', { artista: actual?.artista || '' })"
        />
        <CtaButton
          v-else
          label="Comprar entradas"
          variant="outline"
          :href="destino.href"
          target="_blank"
          rel="noopener noreferrer"
          @click="medir('concierto_entradas', { artista: actual?.artista || '' })"
        />
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
@use "@/assets/scss/type" as t;

.agenda {
  display: grid;
  // 🔴 La altura de la fila la pone el cuadrado de emisión, no esto.
  //
  // Sin `min-height: 0`, el `1fr` del cartel se resuelve por su contenido: el
  // arte es cuadrado, así que con 910 de ancho pedía 910 de alto y estiraba el
  // hero entero a 1005 px. Medido en el navegador el 01.09.26 — a ojo parecía
  // correcto porque la foto llenaba la pantalla.
  min-height: 0;
  // Regla de 2 · cartel · franja de 92. El cartel se queda con lo que sobra,
  // que a 1440 son los 438 del nodo.
  grid-template-rows: 2px 1fr 92px;
  min-width: 0;
  background: var(--color-bg-canvas);
}

// ── La regla de progreso ──
// Misma gramática que `Transport`: pista fuerte, relleno de acento, 2 px.
.agenda__progreso {
  background: var(--color-border-strong);
}

.agenda__hecho {
  display: block;
  height: 100%;
  background: var(--color-bg-accent);
}

// ⚠️ **Sin el gris del arte de la casa.** Aquí va el lienzo de la página, que
// es lo que pidió Pablo el 22.09.26: el gris `art/mid` está pensado para el
// hueco de una tarjeta pequeña, y a media portada durante los segundos que
// tarda un PNG de 4,5 MB se lee como un error. `DuotoneArt` trae ese gris
// consigo, así que se le quita también a él.
.agenda__cartel {
  position: relative;
  min-height: 0;
  background: var(--color-bg-canvas);
  overflow: hidden;

  :deep(.duotone) {
    background: transparent;
  }
}

// ── Mientras el cartel llega ──
// Centrado en el hueco, con su rótulo debajo. Nada de velos ni de fondos: lo
// que hay detrás ya es el lienzo de la página.
.agenda__cargando {
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  pointer-events: none;
}

// El cuadrado que gira: 1 px por los cuatro lados y el de arriba en acento, que
// es lo que hace visible la vuelta. Radio 0, como todo en la casa.
.agenda__giro {
  width: 22px;
  height: 22px;
  border: 1px solid var(--color-border-default);
  border-top-color: var(--color-action-primary-default);
  animation: agenda-girar 900ms linear infinite;
}

.agenda__cargando-texto {
  @include t.mono-micro;
  color: var(--color-text-tertiary);
}

@keyframes agenda-girar {
  to {
    transform: rotate(360deg);
  }
}

// ⚠️ **Con menos movimiento, no gira**: se queda el cuadrado con su canto en
// acento, que sigue diciendo que algo está en marcha sin animar nada.
@media (prefers-reduced-motion: reduce) {
  .agenda__giro {
    animation: none;
  }
}

// ⚠️ **Absolutos los dos.** `DuotoneArt` trae su propia proporción y el hueco
// sin arte crece con su texto: en flujo, cualquiera de los dos le diría a la
// fila cuánto medir, y quien manda aquí es el cuadrado de emisión. Anclados a
// los cuatro lados no tienen alto propio y llenan lo que les toque.
//
// 🔴 `height: 100%` **hace falta**, y no basta con `inset: 0`. `DuotoneArt` trae
// su `aspect-ratio` en línea, y un `aspect-ratio` con la altura en `auto` gana a
// la pareja `top`/`bottom`: la caja se resolvía cuadrada —910 × 910— y el cartel
// enseñaba el tercio de arriba de la foto, que en un retrato es fondo vacío.
// Medido en el navegador; en la captura parecía un cartel en blanco.
.agenda__arte {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;

  // ⚠️ **Aparece entera o no aparece.** Un PNG de 4,5 MB se pinta por franjas
  // de arriba abajo, y verlo construirse por bandas parece un fallo, no una
  // espera. Así que se enseña cuando está.
  //
  // Los 320 son el fundido de un cartel al siguiente, y son los mismos que
  // espera `alCargarElCartel` antes de bajar la capa de abajo: si aquí se
  // cambian, allí también.
  opacity: 0;
  transition: opacity 320ms linear;

  &.is-lista {
    opacity: 1;
  }
}


// ── La franja ──
// ⛔ Sin `overflow: hidden`. Lo llevaba por el truncado del nombre, y de eso ya
// se encarga `agenda__evento`, que tiene el suyo. Lo único que recortaba era el
// filete del pie de la casilla, que necesita salirse 1 px para tapar el borde de
// la franja.
.agenda__franja {
  display: flex;
  align-items: stretch;
  min-width: 0;
  background: var(--color-bg-canvas);
  border-top: 1px solid var(--color-border-default);
  // Sin filete abajo: el hero que la contiene cierra con el suyo a la misma
  // altura, y los dos juntos se leían como una línea de 2 px.
}

// Una línea por junta: el filete lo pone cada celda a su izquierda, así que la
// primera no lleva ninguno y no se duplica con el borde del bloque.
.agenda__franja > * + * {
  border-left: 1px solid var(--color-border-default);
}

// La celda de la fecha. El día en `display/section`, como el nombre del
// artista: son los dos datos que se leen de un vistazo, y en la retícula pesan
// lo mismo. El mes debajo, en la escala de la línea de pie.
//
// ⚠️ Ancho fijo: sin él la celda encoge con «04» y crece con «14», y el nombre
// del artista bailaba de sitio a cada paso del carrusel.
// ⚠️ Ancho fijo: sin él la celda encoge con «04» y crece con «14», y el nombre
// del artista bailaba de sitio a cada paso del carrusel. Y `relative`, que el
// filete del pie de los dos avisos es un `::after` suyo.
.agenda__fecha {
  position: relative;
  display: flex;
  flex: none;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  width: 92px;
  padding: 0 12px;
  background: var(--color-bg-elevated);
}

// La cifra va en **mono**, no en la condensada del display: es un dato que se
// lee de un vistazo y se alinea consigo mismo entre pasos, no un titular.
// `tabular-nums` para que el 1 ocupe lo que el 8.
.agenda__dia {
  font-family: var(--font-mono);
  font-size: 38px;
  font-weight: 400;
  line-height: 0.9;
  color: var(--color-text-primary);
  font-variant-numeric: tabular-nums;
}

.agenda__mes {
  @include t.mono-micro;
  color: var(--color-text-secondary);
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

// ── El aviso de hoy y mañana ──
//
// 🔴 **La palabra va en `text/primary`, no en acento.** El aviso no puede
// depender del color: en acento daría 4,00 sobre `bg/elevated` y a 10 px no pasa
// AA. El rojo entra en 4 px de cuadrado y 2 px de filete, y en ningún sitio más.
//
// El `text-indent` compensa el espacio que el `letter-spacing` deja colgando a
// la derecha de la última letra, que si no descentra la palabra.
.agenda__aviso {
  @include t.mono-label;
  display: flex;
  align-items: center;
  gap: 4px;
  color: var(--color-text-primary);
  letter-spacing: 0.14em;
  text-indent: 0.14em;
  text-transform: uppercase;
  white-space: nowrap;
}

.agenda__aviso i {
  display: block;
  flex: none;
  width: 4px;
  height: 4px;
}

.agenda__fecha.is-manana .agenda__aviso i {
  background: var(--color-text-secondary);
}

.agenda__fecha.is-hoy .agenda__aviso i {
  background: var(--color-icon-accent);
}

// El filete del pie, que es lo que distingue los dos avisos a un metro.
//
// ⚠️ **`bottom: -1px`, y no es un ajuste a ojo.** La franja mide 92 con filete
// arriba y abajo, así que sus celdas miden 90: un filete apoyado en `bottom: 0`
// se queda **encima** del borde de la franja y deja su línea gris asomando por
// debajo. Medido: 1 px exacto. Bajándolo uno, la barra llega al canto y tapa ese
// borde en el ancho de la casilla, que es como lo dibuja el tablero.
.agenda__fecha.is-manana::after,
.agenda__fecha.is-hoy::after {
  content: "";
  position: absolute;
  right: 0;
  bottom: -1px;
  left: 0;
  height: 2px;
}

.agenda__fecha.is-manana::after {
  background: var(--color-border-strong);
}

.agenda__fecha.is-hoy::after {
  background: var(--color-border-accent);
}

// ── La entrada ──
//
// Dos gestos a la vez y ninguno se repite: la palabra se descubre de izquierda a
// derecha y el filete se traza desde la izquierda. **Nada en bucle**: una
// entrada ocurre una vez; un bucle sería una alarma, y esto no lo es.
@keyframes agenda-barrer {
  from {
    clip-path: inset(0 100% 0 0);
  }
  to {
    clip-path: inset(0 0 0 0);
  }
}

@keyframes agenda-trazar {
  from {
    transform: scaleX(0);
  }
  to {
    transform: scaleX(1);
  }
}

.agenda__fecha.is-hoy.is-entrando .agenda__aviso span {
  animation: agenda-barrer 300ms cubic-bezier(0.2, 0.7, 0.2, 1) 120ms both;
}

.agenda__fecha.is-hoy.is-entrando::after {
  transform-origin: left center;
  animation: agenda-trazar 320ms cubic-bezier(0.2, 0.7, 0.2, 1) both;
}

// Se apaga entera y no se pierde nada: la palabra y el filete se ven ya puestos.
// La animación subraya el aviso, no lo contiene.
@media (prefers-reduced-motion: reduce) {
  .agenda__fecha.is-hoy.is-entrando .agenda__aviso span,
  .agenda__fecha.is-hoy.is-entrando::after {
    animation: none;
  }
}

.agenda__evento {
  display: flex;
  flex: 1 0 0;
  flex-direction: column;
  gap: 4px;
  justify-content: center;
  min-width: 0;
  padding: 0 24px;
  overflow: hidden;
}

// 🔴 El nombre del artista, no la fecha, es lo que se lee primero.
//
// La franja tenía dos celdas rotuladas —CUÁNDO y DÓNDE— con el dato en
// `display/row-name`, y **de quién era el concierto no se decía en ninguna
// parte**: había que deducirlo del cartel, que la mitad de las veces no existe.
// El nodo lo rehízo el 01.09.26: titular y pie, como cualquier otra pieza de
// esta casa.
.agenda__artista {
  @include t.display-section;
  margin: 0;
  max-width: 100%;
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.agenda__linea {
  @include t.mono-micro;
  margin: 0;
  max-width: 100%;
  color: var(--color-text-secondary);
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.agenda__paso {
  @include t.mono-micro;
  display: flex;
  flex: none;
  align-items: center;
  margin: 0;
  padding: 0 20px;
  color: var(--color-text-secondary);
  white-space: nowrap;
}

// Las flechas son celdas de la franja, no botones sueltos: misma altura, misma
// línea, sin fondo propio. Es lo que el sistema hace con el control de
// `Transport`.
// De la celda es suyo el alto —lo pone la franja— y su filete izquierdo. El
// resto sale del botón.
//
// ⚠️ Los tres lados, **nunca `border: 0`**: empata en especificidad con la regla
// de junta de arriba y, por ir después, gana; las dos flechas se quedaban sin su
// filete izquierdo. Medido en el navegador el 01.09.26.
.agenda__flecha {
  height: auto;
  align-self: stretch;
  border-top: 0;
  border-right: 0;
  border-bottom: 0;
}

// La celda del botón. Abraza el `cta` de 42 con 24 de aire a cada lado y lo
// centra en los dos ejes; el filete izquierdo es el mismo que separa las demás
// celdas de la franja.
//
// 232 es lo que pide la entrega, y no sale de la nada: el botón mide 184 en el
// nodo y 184 + 24 + 24 son los 232. La celda del evento, que es la que se
// estira, se queda con lo que sobra.
//
// ⚠️ **El hover ya no es de aquí.** Antes esta celda se encendía entera al
// pasar por encima, con su propio `bg-subtle`; ahora quien responde es el
// botón, como en el resto de la casa.
.agenda__entradas {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 232px;
  padding: 0 24px;
  border-left: 1px solid var(--color-border-default);
}

// Con `Brand/Ticketmaster` la celda abraza el botón de 128: 24 + 128 + 24 son
// los 176 de la entrega. El evento se queda con los 56 que sobran.
.agenda__entradas--tm {
  width: 176px;
}

// ── Estrechado ──
//
// ⚠️ A 390 el nodo móvil de la portada **no está actualizado** —lo dice el
// changelog del 01.09.26—, así que aquí no hay diseño que copiar. Lo que se
// hace es lo que hace el resto de la web al estrecharse: la franja se parte en
// dos filas y las celdas de mando bajan juntas. Nada nuevo, solo el mismo
// bloque plegado.
@media (max-width: 880px) {
  .agenda {
    grid-template-rows: 2px auto auto;
  }

  // 🔴 Apilado, el cartel necesita proporción propia. Con el hero en fila la
  // altura se la daba el cuadrado de emisión; en columna no la da nadie, y
  // como el arte va absoluto la fila se resolvía a **cero**: la agenda quedaba
  // reducida a su franja.
  //
  // ⚠️ **436, no los 438 del nodo**: la diferencia son los 2 px de la regla de
  // progreso, que el nodo no descuenta. Las dos disposiciones tienen que decir el
  // mismo número, porque el hero en fila deriva su cuadrado de esta proporción.
  //
  // Estuvo un día en `800/355` —la medida de los carteles que llegaban mal por
  // `conciertos.json`, ver la nota de `HomeView`— y vuelve aquí el 21.09.26.
  .agenda__cartel {
    aspect-ratio: 910 / 436;
  }

  .agenda__franja {
    flex-wrap: wrap;
  }

  .agenda__evento {
    flex: 1 0 100%;
    padding: 14px 20px;
  }

  .agenda__paso,
  .agenda__flecha,
  .agenda__entradas {
    min-height: 56px;
  }

  // Plegada, la celda deja de medir 232: ocupa lo que quede de su fila y el
  // botón se estira con ella. A este ancho, un botón de 184 centrado en una
  // celda de 232 deja la franja descuadrada.
  .agenda__entradas {
    flex: 1 0 0;
    width: auto;
    padding: 0 20px;
  }

  // Al plegar, la primera celda de la segunda fila sí necesita su filete de
  // arriba: la junta ya no es vertical.
  .agenda__franja > * {
    border-top: 1px solid var(--color-border-default);
  }

  .agenda__evento {
    border-top: 0;
  }
}
</style>
